import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { TESTS_PER_PAGE, API_ENDPOINTS } from '../utils/constants';

export const useTests = (selectedStudent, currentMobilePanel, options = {}) => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const { sortBy } = options;  // 'created_desc' 면 실시(생성)일 기준 전체 내림차순 정렬

  // 테스트 이력 관련 상태
  const [testHistory, setTestHistory] = useState([]);
  const [isTestHistoryLoading, setIsTestHistoryLoading] = useState(false);
  const [testHistoryPage, setTestHistoryPage] = useState(1);
  const [hasMoreTests, setHasMoreTests] = useState(true);
  const testHistoryRef = useRef(null);

  // 전체 시험(페이지네이션 이전 원본 전체) — 개념별 "푼 문제" 필터 등에서 사용
  const [allTests, setAllTests] = useState([]);
  
  // 전체 테스트 데이터 캐시 (성능 최적화)
  const allTestsCache = useRef([]);
  const isAllTestsLoaded = useRef(false);
  
  // 결과맵 관련 상태
  const [resultMapData, setResultMapData] = useState(null);
  const [isResultMapLoading, setIsResultMapLoading] = useState(false);
  
  // 리포트 데이터 상태
  const [reportData, setReportData] = useState(null);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [currentReportStampId, setCurrentReportStampId] = useState(null);
  
  // 학생의 테스트 이력 가져오기 (새로운 통합 API 사용)
  const fetchTestHistory = async (studentId, page = 1, reset = false) => {
    try {
      setIsTestHistoryLoading(true);
      
      // 캐시된 데이터가 있고 reset이 아닌 경우 캐시에서 페이지네이션
      if (!reset && isAllTestsLoaded.current && allTestsCache.current.length > 0) {
        const testsPerPage = TESTS_PER_PAGE;
        const startIndex = (page - 1) * testsPerPage;
        const endIndex = startIndex + testsPerPage;
        const paginatedTests = allTestsCache.current.slice(startIndex, endIndex);
        
        setTestHistory(prev => [...prev, ...paginatedTests]);
        setHasMoreTests(endIndex < allTestsCache.current.length);
        setTestHistoryPage(page);
        setIsTestHistoryLoading(false);
        return;
      }
      
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      // reset인 경우 캐시 초기화
      if (reset) {
        allTestsCache.current = [];
        isAllTestsLoaded.current = false;
      }

      // 새로운 통합 API 호출
      const summaryResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${API_ENDPOINTS.EXAMS_SUMMARY}?student_id=${studentId}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (summaryResponse.status === 204 || summaryResponse.status === 404) {
        if (reset) {
          setTestHistory([]);
          allTestsCache.current = [];
        }
        setHasMoreTests(false);
        isAllTestsLoaded.current = true;
        return;
      }

      if (!summaryResponse.ok) {
        throw new Error('학생 테스트 요약 정보를 불러오는데 실패했습니다.');
      }

      const summaryResult = await summaryResponse.json();
      const { exam_cards = [], stamps = [] } = summaryResult.data || {};

      if (stamps.length === 0) {
        if (reset) {
          setTestHistory([]);
          allTestsCache.current = [];
        }
        setHasMoreTests(false);
        isAllTestsLoaded.current = true;
        return;
      }

      // stamps의 exams 배열들을 모두 평탄화하여 allTests 생성
      const allTests = [];
      
      stamps.forEach(stamp => {
        if (stamp.exams && stamp.exams.length > 0) {
          stamp.exams.forEach(exam => {
            // 디버깅: exam_status 200인 시험 확인
            if (exam.exam_status === 200) {
              console.log('📌 미확정 시험 발견:', {
                exam_id: exam.exam_id,
                exam_status: exam.exam_status,
                is_closed: exam.is_closed
              });
            }
            
            // 새 API 응답 구조에 맞게 데이터 변환
            const transformedExam = {
              // 기본 시험 정보
              exam_id: exam.exam_id,
              exam_type: exam.exam_type,
              exam_status: exam.exam_status,
              exam_round: exam.exam_round,
              is_closed: exam.is_closed,
              created_at: exam.created_at,
              updated_at: exam.updated_at,
              
              // 점수 및 통계 정보
              rating: exam.rating || 1000,
              delta: exam.delta || 0,
              correct_count: exam.result ? Object.values(exam.result).filter(r => r === true).length : 0,
              wrong_count: exam.result ? Object.values(exam.result).filter(r => r === false).length : 0,
              total_count: exam.length || 0,
              difficulty: exam.difficulty ? exam.difficulty / 100 : 2.5, // 100 → 1.0 변환
              accuracy: exam.accuracy || 0,
              
              // 문제 및 결과 정보 (problems_info 딕셔너리 활용)
              sequence: (exam.problems || []).map(problemId => [
                problemId,           // problem[0]: problem_id
                exam.problems_info?.[problemId]?.step || null,  // problem[1]: difficulty (step)
                null,               // problem[2]: 기타 정보
                null,               // problem[3]: 기타 정보
                null,               // problem[4]: 기타 정보
                null,               // problem[5]: 기타 정보
                exam.result?.[problemId] || null  // problem[6]: result (정답여부)
              ]),
              problems_list: (exam.problems || []).map(problemId => ({
                problem_id: problemId,
                step: exam.problems_info?.[problemId]?.step || null,      // ✅ problems_info에서 가져오기
                address: exam.problems_info?.[problemId]?.address || null  // ✅ problems_info에서 가져오기
              })),
              result: exam.result || {},
              
              // 레벨별 통계
              by_step: exam.by_step || {},
              
              // 리포트 정보
              report_status: exam.report_info?.is_closed === null ? 0 :     // 리포트 없음
                            exam.report_info?.is_closed === false ? 200 :   // 미확정
                            exam.report_info?.is_closed === true ? 999 : 0,  // 확정완료
              report_info: exam.report_info || { report_id: null, is_closed: null },
              
              // 스탬프 정보 추가
              stamp_id: stamp.stamp_id,
              tree_name: stamp.tree_name,
              edu_ver: stamp.edu_ver,
              stamp_updated_at: exam.updated_at,
              
              // 호환성을 위한 추가 필드들
              exam_created_at: exam.created_at,
              exam_info: {}
            };
            
            allTests.push(transformedExam);
          });
        }
      });

      // 정렬: sortBy='created_desc' 면 실시(생성)일 기준 전체 시계열 내림차순(최근이 최상단),
      // 그 외에는 기존 exam_cards(updated_at) 순서를 따른다.
      let sortedTests;
      if (sortBy === 'created_desc') {
        const ts = (t) => {
          const d = new Date(t.created_at || t.exam_created_at || 0).getTime();
          return Number.isNaN(d) ? 0 : d;
        };
        sortedTests = allTests.slice().sort((a, b) => ts(b) - ts(a) || (b.exam_id - a.exam_id));
      } else {
        // exam_cards 순서를 기준으로 정렬 (updated_at 기준으로 이미 정렬됨)
        const cardOrder = exam_cards.map(card => card.exam_id);
        sortedTests = allTests.sort((a, b) => {
          const indexA = cardOrder.indexOf(a.exam_id);
          const indexB = cardOrder.indexOf(b.exam_id);

          // exam_cards에 있는 것들은 해당 순서대로, 없는 것들은 뒤로
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          } else if (indexA !== -1) {
            return -1;
          } else if (indexB !== -1) {
            return 1;
          } else {
            // 둘 다 exam_cards에 없으면 created_at 기준 최신순
            return new Date(b.created_at) - new Date(a.created_at);
          }
        });
      }

      // 캐시에 저장
      allTestsCache.current = sortedTests;
      isAllTestsLoaded.current = true;
      setAllTests(sortedTests);

      // 페이지네이션 적용
      const testsPerPage = TESTS_PER_PAGE;
      const startIndex = (page - 1) * testsPerPage;
      const endIndex = startIndex + testsPerPage;
      const paginatedTests = sortedTests.slice(startIndex, endIndex);

      if (reset) {
        setTestHistory(paginatedTests);
      } else {
        setTestHistory(prev => [...prev, ...paginatedTests]);
      }

      setHasMoreTests(endIndex < sortedTests.length);
      setTestHistoryPage(page);
    } catch (error) {
      console.error('Error fetching test history:', error);
      if (reset) {
        setTestHistory([]);
        allTestsCache.current = [];
      }
      isAllTestsLoaded.current = true;
    } finally {
      setIsTestHistoryLoading(false);
    }
  };
  
  // 결과맵 데이터 가져오기
  const fetchResultMap = useCallback(async (examId) => {
    if (!examId) return;

    try {
      setIsResultMapLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${API_ENDPOINTS.RESULT_MAP}?exam_id=${examId}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResultMapData(data.data);
    } catch (error) {
      console.error('Error fetching result map:', error);
      setResultMapData(null);
    } finally {
      setIsResultMapLoading(false);
    }
  }, [getAccessToken, navigate]);
  
  // 리포트 데이터 가져오기
  const fetchReportData = useCallback(async (stampId, examId) => {
    try {
      setIsReportLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/report`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        console.log(`No report found for exam ${examId}`);
        return null;
      }

      const reportData = await response.json();
      return reportData.data;
    } catch (error) {
      console.error('Error fetching report data:', error);
      return null;
    } finally {
      setIsReportLoading(false);
    }
  }, [getAccessToken, navigate]);
  
  // 무한 스크롤 핸들러는 TestHistoryPanel에서 직접 처리하므로 useTests에서는 제거됨
  
  // 스크롤 이벤트 리스너는 TestHistoryPanel에서 직접 처리하므로 useTests에서는 제거됨
  
  // 선택된 학생이 변경될 때 테스트 이력 초기화 및 로딩
  useEffect(() => {
    if (selectedStudent) {
      setTestHistory([]);
      setTestHistoryPage(1);
      setHasMoreTests(true);
      setReportData(null);
      setCurrentReportStampId(null);
      // 캐시 초기화
      allTestsCache.current = [];
      isAllTestsLoaded.current = false;
      setAllTests([]);
      fetchTestHistory(selectedStudent.student_id, 1, true);
    } else {
      // 학생 선택이 해제된 경우 캐시 초기화
      setTestHistory([]);
      setReportData(null);
      setCurrentReportStampId(null);
      allTestsCache.current = [];
      isAllTestsLoaded.current = false;
      setAllTests([]);
    }
  }, [selectedStudent]);
  
  // 인피니티 스크롤을 위한 loadMore 함수
  const loadMoreTests = useCallback(() => {
    console.log('🔄 loadMoreTests 호출됨:', {
      hasSelectedStudent: !!selectedStudent,
      studentId: selectedStudent?.student_id,
      hasMoreTests,
      isTestHistoryLoading,
      currentPage: testHistoryPage,
      nextPage: testHistoryPage + 1
    });
    
    if (selectedStudent && hasMoreTests && !isTestHistoryLoading) {
      console.log('✅ 추가 테스트 로드 시작...');
      fetchTestHistory(selectedStudent.student_id, testHistoryPage + 1, false);
    } else {
      console.log('❌ 로드 조건 불만족:', {
        selectedStudent: !!selectedStudent,
        hasMoreTests,
        isTestHistoryLoading
      });
    }
  }, [selectedStudent?.student_id, hasMoreTests, isTestHistoryLoading, testHistoryPage]);

  return {
    // 테스트 이력
    testHistory,
    allTests,
    isTestHistoryLoading,
    testHistoryPage,
    hasMoreTests,
    testHistoryRef,
    fetchTestHistory,
    loadMoreTests,
    
    // 결과맵
    resultMapData,
    isResultMapLoading,
    fetchResultMap,
    
    // 리포트
    reportData,
    isReportLoading,
    currentReportStampId,
    setReportData,
    setCurrentReportStampId,
    fetchReportData
  };
}; 